import { useState, useEffect, useCallback } from 'react';
import { lazy } from 'react';

// Generic type for import functions
type ImportFunction = () => Promise<unknown>;

// Interface for ConfigScreen props
interface ConfigScreenProps {
  onConfigurationSaved?: () => void;
  onCancel?: () => void;
}

// Hook for dynamic screen imports
export function useDynamicScreenImport(agentId: string | undefined, screenFileName: string) {
  const [screenComponent, setScreenComponent] = useState<React.LazyExoticComponent<React.ComponentType<ConfigScreenProps>> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for glob imports
  const [agentScreenModules, setAgentScreenModules] = useState<Record<string, ImportFunction> | null>(null);

  // Initialize glob imports once
  useEffect(() => {
    if (!agentScreenModules) {
      const modules = import.meta.glob('@/agents/*/screens/*.tsx', { eager: false });
      setAgentScreenModules(modules);
    }
  }, [agentScreenModules]);

  const loadScreen = useCallback(async () => {
    if (!agentId || !screenFileName || !agentScreenModules) return;

    setLoading(true);
    setError(null);

    try {
      // Try different path patterns
      const possiblePaths = [
        `/src/agents/${agentId}/screens/${screenFileName}.tsx`,
        `@/agents/${agentId}/screens/${screenFileName}.tsx`,
        `./src/agents/${agentId}/screens/${screenFileName}.tsx`,
      ];

      let importFn: ImportFunction | undefined;
      let foundPath: string | undefined;

      for (const path of possiblePaths) {
        if (agentScreenModules[path]) {
          importFn = agentScreenModules[path];
          foundPath = path;
          break;
        }
      }

      if (!importFn) {
        throw new Error(`Screen not found for agentId: ${agentId}, screenFileName: ${screenFileName}`);
      }

      // Create lazy component
      const LazyComponent = lazy(async () => {
        try {
          const module = await importFn!();
          if (module && typeof module === 'object' && 'default' in module) {
            return module as { default: React.ComponentType<ConfigScreenProps> };
          }
          throw new Error(`Module at ${foundPath} does not have a default export.`);
        } catch (err) {
          console.error(`Error loading module ${foundPath}:`, err);
          throw err;
        }
      });

      setScreenComponent(LazyComponent);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading screen:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [agentId, screenFileName, agentScreenModules]);

  useEffect(() => {
    loadScreen();
  }, [loadScreen]);

  return {
    screenComponent,
    loading,
    error,
    availableModules: agentScreenModules ? Object.keys(agentScreenModules) : [],
  };
}

// Hook for dynamic agent module imports (constants, nodes, etc.)
export function useDynamicAgentModule<T = unknown>(agentId: string | undefined, pattern: 'constants' | 'nodes' | string = 'constants') {
  const [moduleData, setModuleData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Cache for glob imports
  const [agentModules, setAgentModules] = useState<Record<string, ImportFunction> | null>(null);

  // Initialize glob imports once based on pattern
  useEffect(() => {
    if (!agentModules) {
      const modules = import.meta.glob(['@/agents/*/constants/index.ts'], { eager: false });
      setAgentModules(modules);
    }
  }, [agentModules, pattern]);

  const loadModule = useCallback(async () => {
    if (!agentId || !agentModules) return;

    setLoading(true);
    setError(null);

    try {
      // Try different path patterns
      const possiblePaths = [
        `@/agents/${agentId}/constants/index.ts`,
        `/src/agents/${agentId}/constants/index.ts`,
      ];

      let importFn: ImportFunction | undefined;

      for (const path of possiblePaths) {
        if (agentModules[path]) {
          importFn = agentModules[path];
          break;
        }
      }

      if (!importFn) {
        throw new Error(`Module not found for agentId: ${agentId}`);
      }

      const module = await importFn();
      setModuleData(module as T);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      console.error('Error loading agent module:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [agentId, agentModules]);

  useEffect(() => {
    loadModule();
  }, [loadModule]);

  return {
    moduleData,
    loading,
    error,
    availableModules: agentModules ? Object.keys(agentModules) : [],
    refetch: loadModule,
  };
}
